import React, { useEffect, useState, useRef } from "react";
import { api, fmtDate, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, FileText, ExternalLink, Upload } from "lucide-react";

const TYPES = ["Proposal", "Agreement", "Contract", "Invoice", "Quotation", "Presentation", "Event Document", "Marketing Document", "Other"];
const LINK_TYPES = [{ v: "venue", l: "Venue" }, { v: "event", l: "Event" }, { v: "lead", l: "Lead" }, { v: "sponsor", l: "Sponsor" }, { v: "finance", l: "Finance" }];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", type: "Other", url: "", link_type: "", link_id: "" });
  const fileRef = useRef(null);

  const load = async () => {
    const [d, v, e] = await Promise.all([api.get("/documents"), api.get("/venues"), api.get("/events")]);
    setDocs(d.data); setVenues(v.data); setEvents(e.data);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, url: data.url, title: prev.title || data.filename }));
      toast.success("File uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const save = async () => {
    if (!f.title.trim() || !f.url.trim()) return toast.error("Title and URL/file required");
    const opts = f.link_type === "venue" ? venues : f.link_type === "event" ? events : [];
    const linked = opts.find((x) => x.id === f.link_id);
    try {
      await api.post("/documents", { ...f, link_name: linked?.name || null });
      toast.success("Document added");
      setOpen(false); setF({ title: "", type: "Other", url: "", link_type: "", link_id: "" }); load();
    } catch { toast.error("Failed"); }
  };

  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const resolveUrl = (u) => {
    if (!u) return "#";
    if (u.startsWith("http")) return u;
    const token = localStorage.getItem("gl_token");
    return `${backendBase}${u}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  };
  const linkOpts = f.link_type === "venue" ? venues : f.link_type === "event" ? events : [];

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Documents</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Files & Contracts</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-document-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add Document</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Document</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input data-testid="doc-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
              <div><Label>Type</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Link Type</Label><Select value={f.link_type} onValueChange={(v) => setF({ ...f, link_type: v, link_id: "" })}><SelectTrigger><SelectValue placeholder="None" /></SelectTrigger><SelectContent>{LINK_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
              {linkOpts.length > 0 && <div className="col-span-2"><Label>Linked to</Label><Select value={f.link_id} onValueChange={(v) => setF({ ...f, link_id: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{linkOpts.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>}
              <div className="col-span-2"><Label>Google Drive URL (optional)</Label><Input value={f.url} placeholder="https://drive.google.com/..." onChange={(e) => setF({ ...f, url: e.target.value })} /></div>
              <div className="col-span-2">
                <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="file-upload-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4 mr-1" /> Upload file</Button>
                {f.url && !f.url.startsWith("http") && <span className="text-xs text-slate-500 ml-2">Attached: {f.url}</span>}
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="doc-save-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200"><Table>
        <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Linked</TableHead><TableHead>Uploaded</TableHead><TableHead className="text-right">Open</TableHead></TableRow></TableHeader>
        <TableBody>
          {docs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No documents yet. Attach agreements, invoices, or proposals here.</TableCell></TableRow>}
          {docs.map((d) => (
            <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
              <TableCell className="font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" />{d.title}</TableCell>
              <TableCell>{d.type}</TableCell>
              <TableCell className="text-xs text-slate-500">{d.link_type ? `${d.link_type}: ${d.link_name || d.link_id}` : "—"}</TableCell>
              <TableCell>{fmtDate(d.created_at)}</TableCell>
              <TableCell className="text-right"><a href={resolveUrl(d.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm">Open <ExternalLink className="h-3 w-3" /></a></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Card>
    </div>
  );
}
